import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import type { OccupantType } from 'restaurantAdmin/reservations/apiHelpers';
import { errorToast } from '@components/toasts/Toasts';
import { Option } from '@mui/base/Option';
import { Select } from '@mui/base/Select';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { reportAppError } from '@shared/reportAppError';
import { INACTIVE_RESERVATION_STATUSES } from '@shared/types/reservations';
import { INACTIVE_WALK_IN_STATUSES } from '@shared/types/walkIns';
import { useMutation } from '@tanstack/react-query';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import {
  createReservationServiceStatus,
  createWalkInServiceStatus,
  ServiceOccupant,
} from '../apiHelpers';
import { OccupantFinishedConfirmationModal } from '../OccupantFinishedConfirmationModal';
import {
  isStatusVisible,
  SERVICE_STATUS_LABELS,
  SERVICE_STATUS_SELECTION_LIST,
  type ServiceStatus,
} from './ServiceStatus';
import { ServiceStatusIcon } from './ServiceStatusIcon';
import styles from './ServiceStatusSelect.scss';

export interface ServiceStatusSelectProps {
  occupant: Pick<ServiceOccupant, 'id' | 'type' | 'status'>;
  onStatusChange?: (status: ServiceStatus) => void;
  showLabel?: boolean;
  value: ServiceStatus;
}

export const ServiceStatusSelect = ({
  occupant,
  onStatusChange,
  showLabel = false,
  value,
}: ServiceStatusSelectProps) => {
  const { isOpen, close, open } = useIsOpen();
  const createServiceStatusMutation = useCreateServiceStatus({
    occupantType: occupant.type,
    onSuccess: onStatusChange,
  });

  const onChange = (_event: unknown, newValue: ServiceStatus | null) => {
    if (!newValue) return;
    if (newValue === 'finished') {
      open();
      return;
    }

    createServiceStatusMutation.mutate({
      occupantId: occupant.id,
      serviceStatus: newValue,
    });
  };

  const occupantHasInactiveStatus = [
    ...INACTIVE_RESERVATION_STATUSES,
    ...INACTIVE_WALK_IN_STATUSES,
  ].includes(occupant.status);

  return (
    <>
      <div className={styles.main}>
        <Select<ServiceStatus>
          aria-label="Service Status"
          defaultValue={value}
          disabled={
            occupantHasInactiveStatus || createServiceStatusMutation.isPending
          }
          onChange={onChange}
          renderValue={(val) => {
            if (val && showLabel) {
              return (
                <div className={styles.iconWithLabel}>
                  <ServiceStatusIcon serviceStatus={val.value} />
                  <span className={cx(typography.t3, styles.statusLabel)}>
                    {SERVICE_STATUS_LABELS[val.value]}
                  </span>
                </div>
              );
            }
            if (val) {
              return <ServiceStatusIcon serviceStatus={val.value} />;
            }
            return null;
          }}
          slotProps={{
            root: { className: styles.combobox },
            popup: { disablePortal: true, style: { zIndex: 10 } },
            listbox: { className: styles.listbox },
          }}
          value={value}
        >
          {SERVICE_STATUS_SELECTION_LIST.map((status) =>
            isStatusVisible(status) ? (
              <Option value={status} className={styles.option} key={status}>
                <ServiceStatusIcon aria-hidden serviceStatus={status} />
                <span className={cx(typography.t1, styles.statusName)}>
                  {SERVICE_STATUS_LABELS[status]}
                </span>
              </Option>
            ) : (
              <Option aria-hidden value={status} key={status} />
            ),
          )}
        </Select>
      </div>
      <OccupantFinishedConfirmationModal
        isOpen={isOpen}
        closeModal={close}
        occupant={{ id: occupant.id, type: occupant.type }}
      />
    </>
  );
};

const useCreateServiceStatus = ({
  occupantType,
  onSuccess,
}: {
  occupantType: OccupantType;
  onSuccess?: (serviceStatus: ServiceStatus) => void;
}) => {
  const { id: restaurantId } = useRestaurant();
  const { createServiceStatus } = occupantTypeHelpers[occupantType];

  return useMutation({
    mutationFn: async ({
      occupantId,
      serviceStatus,
    }: {
      occupantId: string;
      serviceStatus: ServiceStatus;
    }) => {
      if (!serviceStatus) return;
      await createServiceStatus(restaurantId, occupantId, serviceStatus);
    },
    onSuccess: (_data, variables) => {
      const { serviceStatus } = variables;
      onSuccess?.(serviceStatus);
    },
    onError: (error, variables) => {
      const { serviceStatus } = variables;
      errorToast({
        message: `Failed to update the service status to "${SERVICE_STATUS_LABELS[serviceStatus]}"`,
      });
      reportAppError(error);
    },
  });
};

const occupantTypeHelpers: Record<
  OccupantType,
  {
    createServiceStatus: (
      restaurantId: string,
      occupantId: string,
      serviceStatus: ServiceStatus,
    ) => Promise<void>;
  }
> = {
  reservation: {
    createServiceStatus: createReservationServiceStatus,
  },
  walkIn: {
    createServiceStatus: createWalkInServiceStatus,
  },
} as const;
